import { Loader2 } from 'lucide-react'
import { cn } from '@lib/utils/helpers'

/** Brand spinner used across list/detail module loading states (invitee / PMC parity). */
export const MODULE_LOADING_SPINNER_CLASS =
  'h-8 w-8 animate-spin text-[rgb(21_151_255)]'

type ModuleScreenLoadingProps = {
  /** Merged with the outer container. */
  className?: string
  'data-testid'?: string
  /** Optional message shown below the spinner. */
  message?: string
  messageTestId?: string
  /**
   * `card` — centered inside a list/results card (default, invitee parity).
   * `page` — centered in the main content area below a page header.
   * `inline` — compact centered block for modals / nested panels.
   */
  variant?: 'card' | 'page' | 'inline'
  /** Minimum height for the card variant. */
  minHeight?: string
}

function ModuleLoadingSpinner({
  'data-testid': dataTestId,
}: {
  'data-testid'?: string
}) {
  return (
    <Loader2
      className={MODULE_LOADING_SPINNER_CLASS}
      aria-hidden
      data-testid={dataTestId ? `${dataTestId}-spinner` : undefined}
    />
  )
}

/** Canonical loading indicator for dashboard list/detail modules. */
export function ModuleScreenLoading({
  className,
  'data-testid': dataTestId,
  message,
  messageTestId,
  variant = 'card',
  minHeight = '280px',
}: ModuleScreenLoadingProps) {
  const spinner = <ModuleLoadingSpinner data-testid={dataTestId} />

  if (variant === 'page') {
    return (
      <div
        data-testid={dataTestId}
        className={cn(
          'flex min-h-[50vh] flex-col items-center justify-center px-6 py-20',
          className,
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {spinner}
        {message ? (
          <p
            data-testid={messageTestId}
            className="mt-4 text-sm font-medium text-slate-600"
          >
            {message}
          </p>
        ) : (
          <span className="sr-only">Loading</span>
        )}
      </div>
    )
  }

  if (variant === 'inline') {
    return (
      <div
        data-testid={dataTestId}
        className={cn(
          'flex flex-col items-center justify-center py-12',
          className,
        )}
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        {spinner}
        {message ? (
          <p
            data-testid={messageTestId}
            className="mt-3 text-sm font-medium text-slate-600"
          >
            {message}
          </p>
        ) : (
          <span className="sr-only">Loading</span>
        )}
      </div>
    )
  }

  return (
    <div
      data-testid={dataTestId}
      className={cn('flex flex-1 items-center justify-center py-20', className)}
      style={{ minHeight }}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <div className="flex flex-col items-center">
        {spinner}
        {message ? (
          <p
            data-testid={messageTestId}
            className="mt-4 text-sm font-medium text-slate-600"
          >
            {message}
          </p>
        ) : (
          <span className="sr-only">Loading</span>
        )}
      </div>
    </div>
  )
}

type ListingTableLoadingRowProps = {
  colSpan: number
  className?: string
  'data-testid'?: string
}

/** PMC/company-list parity: blue spinner centered in a table body row. */
export function ListingTableLoadingRow({
  colSpan,
  className,
  'data-testid': dataTestId,
}: ListingTableLoadingRowProps) {
  return (
    <tr data-testid={dataTestId}>
      <td colSpan={colSpan} className={cn('!py-16', className)}>
        <div
          className="flex items-center justify-center"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <ModuleLoadingSpinner data-testid={dataTestId} />
          <span className="sr-only">Loading</span>
        </div>
      </td>
    </tr>
  )
}

type ModuleScreenErrorProps = {
  message: string
  className?: string
  'data-testid'?: string
  messageTestId?: string
}

/** Canonical inline error panel for list/results cards (invitee parity). */
export function ModuleScreenError({
  message,
  className,
  'data-testid': dataTestId,
  messageTestId,
}: ModuleScreenErrorProps) {
  return (
    <div
      data-testid={dataTestId}
      className={cn(
        'm-4 rounded-lg bg-red-50 p-4 text-center text-red-600 sm:p-8',
        className,
      )}
    >
      <p data-testid={messageTestId} className="text-sm sm:text-base">
        {message}
      </p>
    </div>
  )
}
